/**
 * Baseball (rules: app/LagKing/BASEBALL.md, decided with Cole 2026-10-08).
 *
 * A pure reducer, like the other two-player games: state = fold(putts), so
 * local play, the CPU and (later) live online share it. The engine never
 * sees a raw putt; the caller reads it against every hole first
 * (baseballRead.ts) so the make model stays in one place.
 *
 * Teams: 0 = away (bats in the top half), 1 = home (the challenger; bats
 * last). Bases are [first, second, third].
 */

/** Single, double, triple, home run. Moved back 5 ft from 10/20/30/40 on 2026-10-08 (Cole); the bunt stayed at 5. */
export const BASEBALL_HIT_HOLES_FT = [15, 25, 35, 45] as const;
export const BASEBALL_BUNT_HOLE_FT = 5;
export const BASEBALL_RING_FT = 3;
/** The sacrifice-fly band is this wide, just outside the hole's hit tolerance
 *  (3–5 ft, then 1.5–3.5 ft, then up to 2 ft at make-only; Cole, 2026-10-16). */
export const BASEBALL_SAC_FLY_BAND_FT = 2;
/** On a sacrifice fly a fielder finishing this close holds the runner on
 *  third: the batter is out and nobody scores. Holing it doubles the runner
 *  off (Cole, 2026-10-16). */
export const BASEBALL_SAC_FLY_HOLD_FT = 1.5;

/**
 * Progressive hit tolerance (anti-farming, Cole 2026-10-16). Each hit type
 * tightens for the batting team every time it is earned: within 3 ft, then
 * within 1.5 ft, then it must be holed, then it is unavailable. Earning one of
 * every type (an unavailable type counts: it was earned three times) resets
 * all four to 3 ft. Stage 3 = unavailable.
 */
export const BASEBALL_HIT_STAGE_TOLERANCE_FT = [3, 1.5, 0] as const;
export type BaseballHitStage = 0 | 1 | 2 | 3;
export type BaseballHitStages = [BaseballHitStage, BaseballHitStage, BaseballHitStage, BaseballHitStage];
export const BASEBALL_DEFAULT_INNINGS = 3;

export type BaseballHitHoleFt = (typeof BASEBALL_HIT_HOLES_FT)[number];
export type BaseballHoleFt = BaseballHitHoleFt | typeof BASEBALL_BUNT_HOLE_FT;
export type BaseballTeam = 0 | 1;
export type BaseballHalf = 'top' | 'bottom';
export type BaseballBases = [boolean, boolean, boolean];
export type BaseballExtraFormat = 'atBat' | 'inning';
export type BaseballPhase = 'batting' | 'fielding' | 'extraChoice' | 'complete';

/** One putt read against every hole (baseballRead.ts builds it). */
export type BaseballRead = Record<BaseballHoleFt, { leaveFt: number; holed: boolean }>;

export type BaseballPlayKind =
  | 'single' | 'double' | 'triple' | 'homeRun'
  | 'out' | 'fieldedOut'
  | 'sacBunt' | 'sacFly' | 'flyOut'
  | 'doublePlay' | 'flyDoublePlay';

/** 0 = home plate (the batter), 1–3 = the bases, 4 = scored. */
export interface BaseballMove {
  from: 0 | 1 | 2 | 3;
  to: 1 | 2 | 3 | 4 | 'out';
}

export interface BaseballPlay {
  kind: BaseballPlayKind;
  team: BaseballTeam;
  hole: BaseballHoleFt | null;
  /** The batter holed it (a hit no fielder could answer). */
  holed: boolean;
  /** Lead runner first, the batter last: the order the diamond animates them. */
  moves: BaseballMove[];
  runs: number;
  outs: number;
}

/** What the batter's ball left for the fielder to answer. */
export type BaseballPending =
  | { kind: 'hit'; hole: BaseballHitHoleFt; leaveFt: number }
  | { kind: 'sacFly'; hole: BaseballHitHoleFt }
  | { kind: 'groundDp'; hole: BaseballHitHoleFt };

export interface BaseballState {
  innings: number;
  inning: number;
  half: BaseballHalf;
  outs: number;
  bases: BaseballBases;
  runs: [number, number];
  /** Runs per inning per team; an inning a team has not batted in is absent. */
  lineScore: [number[], number[]];
  phase: BaseballPhase;
  pending: BaseballPending | null;
  lastPlay: BaseballPlay | null;
  /** Extra frames: one at-bat each, the better outcome wins. */
  swingOff: { outcomes: [number | null, number | null] } | null;
  winner: BaseballTeam | null;
  decidedBy: 'runs' | 'swingOff' | null;
  atBats: number;
  /** Box-score counts by team (a swing-off adds none, as it adds no runs). */
  hits: [number, number];
  /** Hits taken away by the fielder, credited to the fielding team. */
  blocks: [number, number];
  /** Double plays turned, credited to the fielding team. */
  doublePlays: [number, number];
  /** Each team's hit stage per hit hole (single, double, triple, home run).
   *  Absent from a game saved before 2026-10-16: read it with baseballHitStages. */
  hitStages?: [BaseballHitStages, BaseballHitStages];
  /** Hit types each team has earned since its last reset. */
  hitsEarned?: [[boolean, boolean, boolean, boolean], [boolean, boolean, boolean, boolean]];
}

export type BaseballEvent =
  | { type: 'pending'; pending: BaseballPending }
  | { type: 'play'; play: BaseballPlay }
  | { type: 'sideRetired' }
  | { type: 'extraChoice' }
  | { type: 'gameOver'; winner: BaseballTeam };

export interface AppliedBaseballPutt {
  state: BaseballState;
  events: BaseballEvent[];
}

const HIT_KIND: Record<BaseballHitHoleFt, 'single' | 'double' | 'triple' | 'homeRun'> = { 15: 'single', 25: 'double', 35: 'triple', 45: 'homeRun' };
const HIT_BASES: Record<BaseballHitHoleFt, 1 | 2 | 3 | 4> = { 15: 1, 25: 2, 35: 3, 45: 4 };
const SINGLE_HOLE_FT = BASEBALL_HIT_HOLES_FT[0];
const SAC_FLY_HOLES_FT = [BASEBALL_HIT_HOLES_FT[2], BASEBALL_HIT_HOLES_FT[3]] as const;

/** How many bases a hole is worth (4 = home run). */
export function baseballHoleBases(hole: BaseballHitHoleFt): 1 | 2 | 3 | 4 {
  return HIT_BASES[hole];
}

/** The hit a hole stands for. */
export function baseballHoleKind(hole: BaseballHitHoleFt): 'single' | 'double' | 'triple' | 'homeRun' {
  return HIT_KIND[hole];
}
/** Swing-off ranking: home run > triple > double > single > sacrifice > out. */
const OUTCOME_RANK: Record<BaseballPlayKind, number> = {
  homeRun: 5, triple: 4, double: 3, single: 2, sacBunt: 1, sacFly: 1, flyOut: 0, out: 0, fieldedOut: 0, doublePlay: 0, flyDoublePlay: 0,
};

export function createBaseballGame(innings: number = BASEBALL_DEFAULT_INNINGS): BaseballState {
  return {
    innings: Math.max(1, Math.floor(innings)),
    inning: 1,
    half: 'top',
    outs: 0,
    bases: [false, false, false],
    runs: [0, 0],
    lineScore: [[0], []],
    phase: 'batting',
    pending: null,
    lastPlay: null,
    swingOff: null,
    winner: null,
    decidedBy: null,
    atBats: 0,
    hits: [0, 0],
    blocks: [0, 0],
    doublePlays: [0, 0],
    hitStages: [[0, 0, 0, 0], [0, 0, 0, 0]],
    hitsEarned: [[false, false, false, false], [false, false, false, false]],
  };
}

/** A team's hit stage for each hit hole (0 = 3 ft … 3 = unavailable). */
export function baseballHitStages(state: BaseballState, team: BaseballTeam): BaseballHitStages {
  return state.hitStages?.[team] ?? [0, 0, 0, 0];
}

/** The stage of one hit hole for a team. */
export function baseballHitStage(state: BaseballState, team: BaseballTeam, hole: BaseballHitHoleFt): BaseballHitStage {
  return baseballHitStages(state, team)[BASEBALL_HIT_HOLES_FT.indexOf(hole)];
}

/** How close counts as the hit: 3, 1.5, 0 (holed only), or null (unavailable). */
export function baseballHitToleranceFt(stage: BaseballHitStage): number | null {
  return stage === 3 ? null : BASEBALL_HIT_STAGE_TOLERANCE_FT[stage];
}

/** What a hit needs at a stage, for the scoreboards: "3 FT", "1.5 FT", "HOLE IT", or "OFF". */
export function baseballHitNeedLabel(stage: BaseballHitStage): string {
  if (stage === 3) return 'OFF';
  if (stage === 2) return 'HOLE IT';
  return `${BASEBALL_HIT_STAGE_TOLERANCE_FT[stage]} FT`;
}

/** The sacrifice-fly band for a stage: past the hit tolerance, never past it by more than 2 ft. */
export function baseballSacFlyBandFt(stage: BaseballHitStage): { minFt: number; maxFt: number } | null {
  const tolerance = baseballHitToleranceFt(stage);
  return tolerance === null ? null : { minFt: tolerance, maxFt: tolerance + BASEBALL_SAC_FLY_BAND_FT };
}

/** The batter earned this hit: tighten it, and reset all four once every type is earned. */
function earnHit(state: BaseballState, team: BaseballTeam, hole: BaseballHitHoleFt): void {
  const index = BASEBALL_HIT_HOLES_FT.indexOf(hole);
  const stages = [baseballHitStages(state, 0), baseballHitStages(state, 1)].map((row) => [...row]) as [BaseballHitStages, BaseballHitStages];
  const earned = (state.hitsEarned ?? [[false, false, false, false], [false, false, false, false]])
    .map((row) => [...row]) as [[boolean, boolean, boolean, boolean], [boolean, boolean, boolean, boolean]];
  stages[team][index] = Math.min(3, stages[team][index] + 1) as BaseballHitStage;
  earned[team][index] = true;
  if (earned[team].every(Boolean)) {
    stages[team] = [0, 0, 0, 0];
    earned[team] = [false, false, false, false];
  }
  state.hitStages = stages;
  state.hitsEarned = earned;
}

export function baseballBattingTeam(state: BaseballState): BaseballTeam {
  return state.half === 'top' ? 0 : 1;
}

export function baseballFieldingTeam(state: BaseballState): BaseballTeam {
  return state.half === 'top' ? 1 : 0;
}

/** Whose putt is next; null when nobody putts (choosing a format, game over). */
export function baseballPutter(state: BaseballState): BaseballTeam | null {
  if (state.phase === 'batting') return baseballBattingTeam(state);
  if (state.phase === 'fielding') return baseballFieldingTeam(state);
  return null;
}

export function baseballRunnersOn(state: BaseballState): boolean {
  return state.bases.some(Boolean);
}

/**
 * The 5-ft bunt cup shows only with runners on, fewer than two outs, and a
 * base open: no sacrifice bunt with the bases loaded (Cole, 2026-10-08).
 */
export function baseballBuntOn(state: BaseballState): boolean {
  return state.phase === 'batting' && !state.swingOff && state.outs < 2 && baseballRunnersOn(state) && !state.bases.every(Boolean);
}

/** A sacrifice fly is on: runner on third, fewer than two outs. The holes it can come off of. */
export function baseballSacFlyHolesFt(state: BaseballState): readonly BaseballHitHoleFt[] {
  if (!(state.phase === 'batting' && !state.swingOff && state.outs < 2 && state.bases[2])) return [];
  const team = baseballBattingTeam(state);
  return SAC_FLY_HOLES_FT.filter((hole) => baseballHitStage(state, team, hole) < 3);
}

/** The distance the fielder answers from; null while the batter is up. */
export function baseballFielderDistanceFt(state: BaseballState): BaseballHitHoleFt | null {
  return state.phase === 'fielding' && state.pending ? state.pending.hole : null;
}

export function baseballPlayLabel(kind: BaseballPlayKind): string {
  switch (kind) {
    case 'single': return 'SINGLE';
    case 'double': return 'DOUBLE';
    case 'triple': return 'TRIPLE';
    case 'homeRun': return 'HOME RUN';
    case 'out': return 'OUT';
    case 'fieldedOut': return 'OUT';
    case 'sacBunt': return 'SAC BUNT';
    case 'sacFly': return 'SAC FLY';
    case 'flyOut': return 'FLY OUT';
    case 'doublePlay': return 'DOUBLE PLAY';
    case 'flyDoublePlay': return 'DOUBLE PLAY';
  }
}

function clone(state: BaseballState): BaseballState {
  return {
    ...state,
    bases: [...state.bases] as BaseballBases,
    runs: [...state.runs] as [number, number],
    lineScore: [[...state.lineScore[0]], [...state.lineScore[1]]],
    hits: [...state.hits] as [number, number],
    blocks: [...state.blocks] as [number, number],
    doublePlays: [...state.doublePlays] as [number, number],
    ...(state.hitStages ? { hitStages: [[...state.hitStages[0]], [...state.hitStages[1]]] as [BaseballHitStages, BaseballHitStages] } : {}),
    ...(state.hitsEarned ? { hitsEarned: [[...state.hitsEarned[0]], [...state.hitsEarned[1]]] as [[boolean, boolean, boolean, boolean], [boolean, boolean, boolean, boolean]] } : {}),
    swingOff: state.swingOff ? { outcomes: [...state.swingOff.outcomes] as [number | null, number | null] } : null,
  };
}

/**
 * The runner a ground-ball double play retires along with the batter (Cole,
 * 2026-10-15): the runner from first in every force situation (first; first
 * and second, where the runner from second reaches third; first and third,
 * where the runner on third scores) except bases loaded, where the play goes
 * home to first and the runner from third is out.
 */
function doublePlayRunnerBase(bases: BaseballBases): 1 | 3 | null {
  if (!bases[0]) return null;
  return bases[1] && bases[2] ? 3 : 1;
}

/** Every runner (lead first) moves `count` bases; `skip` is a base whose runner was just put out. */
function advanceRunners(bases: BaseballBases, count: number, skip: number | null = null): { bases: BaseballBases; moves: BaseballMove[]; runs: number } {
  const next: BaseballBases = [false, false, false];
  const moves: BaseballMove[] = [];
  let runs = 0;
  for (let base = 3; base >= 1; base -= 1) {
    if (!bases[base - 1] || base === skip) continue;
    const to = base + count;
    if (to >= 4) {
      runs += 1;
      moves.push({ from: base as 1 | 2 | 3, to: 4 });
    } else {
      next[to - 1] = true;
      if (to !== base) moves.push({ from: base as 1 | 2 | 3, to: to as 1 | 2 | 3 });
    }
  }
  return { bases: next, moves, runs };
}

function addRuns(state: BaseballState, team: BaseballTeam, runs: number): void {
  if (runs <= 0 || state.swingOff) return;
  state.runs[team] += runs;
  const line = state.lineScore[team];
  line[state.inning - 1] = (line[state.inning - 1] ?? 0) + runs;
}

function finish(state: BaseballState, winner: BaseballTeam, decidedBy: 'runs' | 'swingOff', events: BaseballEvent[]): void {
  state.phase = 'complete';
  state.winner = winner;
  state.decidedBy = decidedBy;
  state.pending = null;
  events.push({ type: 'gameOver', winner });
}

function startHalf(state: BaseballState, half: BaseballHalf, inning: number): void {
  state.half = half;
  state.inning = inning;
  state.outs = 0;
  state.bases = [false, false, false];
  state.phase = 'batting';
  state.pending = null;
  const line = state.lineScore[half === 'top' ? 0 : 1];
  if (line[inning - 1] === undefined) line[inning - 1] = 0;
}

function retireSide(state: BaseballState, events: BaseballEvent[]): void {
  events.push({ type: 'sideRetired' });
  const last = state.inning >= state.innings;
  if (state.half === 'top') {
    // The home team does not bat in a last inning it already leads.
    if (last && state.runs[1] > state.runs[0]) {
      finish(state, 1, 'runs', events);
      return;
    }
    startHalf(state, 'bottom', state.inning);
    return;
  }
  if (!last) {
    startHalf(state, 'top', state.inning + 1);
    return;
  }
  if (state.runs[0] !== state.runs[1]) {
    finish(state, state.runs[0] > state.runs[1] ? 0 : 1, 'runs', events);
    return;
  }
  state.outs = 0;
  state.bases = [false, false, false];
  state.pending = null;
  state.phase = 'extraChoice';
  events.push({ type: 'extraChoice' });
}

/** Close an at-bat: record the play, then walk-off, three outs, or the next batter. */
function settle(state: BaseballState, play: BaseballPlay, events: BaseballEvent[]): void {
  state.lastPlay = play;
  state.pending = null;
  state.phase = 'batting';
  state.atBats += 1;
  events.push({ type: 'play', play });

  if (state.swingOff) {
    state.swingOff.outcomes[play.team] = OUTCOME_RANK[play.kind];
    state.bases = [false, false, false];
    state.outs = 0;
    if (play.team === 0) {
      state.half = 'bottom';
      return;
    }
    const [away, home] = state.swingOff.outcomes as [number, number];
    if (away !== home) {
      finish(state, away > home ? 0 : 1, 'swingOff', events);
      return;
    }
    // Still level: the players choose again.
    state.swingOff = null;
    state.half = 'top';
    state.phase = 'extraChoice';
    events.push({ type: 'extraChoice' });
    return;
  }

  // Walk-off: the home team takes the lead in the bottom of a last inning.
  if (state.half === 'bottom' && state.inning >= state.innings && state.runs[1] > state.runs[0]) {
    finish(state, 1, 'runs', events);
    return;
  }
  if (state.outs >= 3) retireSide(state, events);
}

function hit(state: BaseballState, hole: BaseballHitHoleFt, holed: boolean, events: BaseballEvent[]): void {
  const team = baseballBattingTeam(state);
  const count = HIT_BASES[hole];
  // A holed hit moves every runner one base further (Cole, 2026-10-15: "a made
  // single scores a runner from 2nd, made double scores a runner from 1st").
  const advanced = advanceRunners(state.bases, holed ? count + 1 : count);
  const moves = [...advanced.moves, { from: 0, to: count } as BaseballMove];
  const runs = advanced.runs + (count === 4 ? 1 : 0);
  state.bases = advanced.bases;
  if (count < 4) state.bases[count - 1] = true;
  addRuns(state, team, runs);
  if (!state.swingOff) {
    state.hits[team] += 1;
    earnHit(state, team, hole);
  }
  settle(state, { kind: HIT_KIND[hole], team, hole, holed, moves, runs, outs: 0 }, events);
}

function plainOut(state: BaseballState, kind: 'out' | 'fieldedOut', hole: BaseballHoleFt | null, events: BaseballEvent[]): void {
  const team = baseballBattingTeam(state);
  state.outs += 1;
  if (kind === 'fieldedOut' && !state.swingOff) state.blocks[baseballFieldingTeam(state)] += 1;
  settle(state, { kind, team, hole, holed: false, moves: [{ from: 0, to: 'out' }], runs: 0, outs: 1 }, events);
}

function nearestHitHole(read: BaseballRead): BaseballHitHoleFt {
  return BASEBALL_HIT_HOLES_FT.reduce((best, hole) => (read[hole].leaveFt < read[best].leaveFt ? hole : best), BASEBALL_HIT_HOLES_FT[0] as BaseballHitHoleFt);
}

/**
 * The ball stopped alongside the stretch between the single and the double:
 * its foot on the single-to-double line falls between the two holes. Read from
 * the two leaves alone (both angles at the holes are acute), so a putt that
 * drifted wide still counts by where it stopped along the line.
 */
function betweenSingleAndDouble(read: BaseballRead): boolean {
  const gap = BASEBALL_HIT_HOLES_FT[1] - SINGLE_HOLE_FT;
  const toSingle = read[SINGLE_HOLE_FT].leaveFt;
  const toDouble = read[BASEBALL_HIT_HOLES_FT[1]].leaveFt;
  return toDouble ** 2 < toSingle ** 2 + gap ** 2 && toSingle ** 2 < toDouble ** 2 + gap ** 2;
}

function applyBatter(state: BaseballState, read: BaseballRead, events: BaseballEvent[], gateStrike: boolean): void {
  const team = baseballBattingTeam(state);
  const stageOf = (hole: BaseballHitHoleFt) => baseballHitStage(state, team, hole);
  // An unavailable hit counts for nothing, holed or not.
  const holedHole = BASEBALL_HIT_HOLES_FT.find((hole) => read[hole].holed && stageOf(hole) < 3);
  if (holedHole !== undefined) {
    hit(state, holedHole, true, events);
    return;
  }
  // Sacrifice bunt: only the 5-ft cup, only holed, only with runners on and under two outs.
  if (baseballBuntOn(state) && read[BASEBALL_BUNT_HOLE_FT].holed) {
    const advanced = advanceRunners(state.bases, 1);
    state.bases = advanced.bases;
    state.outs += 1;
    addRuns(state, team, advanced.runs);
    settle(state, {
      kind: 'sacBunt', team, hole: BASEBALL_BUNT_HOLE_FT, holed: true,
      moves: [...advanced.moves, { from: 0, to: 'out' }], runs: advanced.runs, outs: 1,
    }, events);
    return;
  }
  // Inside the hole's current tolerance (3 ft, then 1.5 ft; a make-only or
  // unavailable hole has no ring) is a provisional hit for the fielder to answer.
  const ringHole = BASEBALL_HIT_HOLES_FT
    .filter((hole) => {
      const tolerance = baseballHitToleranceFt(stageOf(hole));
      return tolerance !== null && tolerance > 0 && read[hole].leaveFt <= tolerance;
    })
    .sort((a, b) => read[a].leaveFt - read[b].leaveFt)[0];
  if (ringHole !== undefined) {
    state.pending = { kind: 'hit', hole: ringHole, leaveFt: read[ringHole].leaveFt };
    state.phase = 'fielding';
    events.push({ type: 'pending', pending: state.pending });
    return;
  }
  if (!state.swingOff && state.outs < 2) {
    // Sacrifice fly: in the 2-ft band just outside the triple's or home run's
    // current tolerance, with a runner on third. A holed ball is never one.
    if (state.bases[2]) {
      const flyHole = SAC_FLY_HOLES_FT.find((hole) => {
        const band = baseballSacFlyBandFt(stageOf(hole));
        return band !== null && !read[hole].holed && read[hole].leaveFt > band.minFt && read[hole].leaveFt <= band.maxFt;
      });
      if (flyHole !== undefined) {
        state.pending = { kind: 'sacFly', hole: flyHole };
        state.phase = 'fielding';
        events.push({ type: 'pending', pending: state.pending });
        return;
      }
    }
    // Double-play ball: a runner on first, under two outs, and a batter's ball
    // that earned no hit and stopped nearest the single or past it but short
    // of the double (Cole, 2026-10-15; 2026-10-19: "only near the single and
    // short of the double"). Inside that range every non-hit counts, a ball in
    // the single's tightened or make-only ring or at a used-up single's spot
    // included (Cole, 2026-10-19: "always a double play chance"; the
    // 2026-10-16 original-ring exception is gone). A miss near the triple or
    // the home run is a plain out, and so is a gate strike (the ball never
    // reached the single). The fielder answers from the single's 15 ft.
    if (state.bases[0] && !gateStrike && (nearestHitHole(read) === SINGLE_HOLE_FT || betweenSingleAndDouble(read))) {
      state.pending = { kind: 'groundDp', hole: SINGLE_HOLE_FT };
      state.phase = 'fielding';
      events.push({ type: 'pending', pending: state.pending });
      return;
    }
  }
  plainOut(state, 'out', null, events);
}

function applyFielder(state: BaseballState, read: BaseballRead, events: BaseballEvent[]): void {
  const pending = state.pending!;
  const team = baseballBattingTeam(state);
  const answer = read[pending.hole];

  if (pending.kind === 'hit') {
    // Inside the batter's leave turns the hit into an out; a tie goes to the runner.
    if (answer.holed || answer.leaveFt < pending.leaveFt) plainOut(state, 'fieldedOut', pending.hole, events);
    else hit(state, pending.hole, false, events);
    return;
  }

  if (pending.kind === 'sacFly') {
    if (answer.holed) {
      // Holed: the runner tagging from third is doubled off; nobody scores,
      // the other runners still move up.
      state.outs += 2;
      state.doublePlays[baseballFieldingTeam(state)] += 1;
      const advanced = state.outs < 3 ? advanceRunners(state.bases, 1, 3) : { bases: [false, false, false] as BaseballBases, moves: [], runs: 0 };
      state.bases = advanced.bases;
      settle(state, {
        kind: 'flyDoublePlay', team, hole: pending.hole, holed: false,
        moves: [{ from: 3, to: 'out' }, { from: 0, to: 'out' }, ...advanced.moves], runs: 0, outs: 2,
      }, events);
      return;
    }
    if (answer.leaveFt <= BASEBALL_SAC_FLY_HOLD_FT) {
      // Inside 1.5 ft: the fly is caught short. The batter is out, every
      // runner holds, no run scores.
      state.outs += 1;
      if (!state.swingOff) state.blocks[baseballFieldingTeam(state)] += 1;
      settle(state, { kind: 'flyOut', team, hole: pending.hole, holed: false, moves: [{ from: 0, to: 'out' }], runs: 0, outs: 1 }, events);
      return;
    }
    state.outs += 1;
    const advanced = advanceRunners(state.bases, 1);
    state.bases = advanced.bases;
    addRuns(state, team, advanced.runs);
    settle(state, {
      kind: 'sacFly', team, hole: pending.hole, holed: false,
      moves: [...advanced.moves, { from: 0, to: 'out' }], runs: advanced.runs, outs: 1,
    }, events);
    return;
  }

  // Ground ball: holed from the single's distance turns it over. The batter
  // and one runner (doublePlayRunnerBase) are out; everyone else moves up one
  // base, and a runner reaching home scores unless that made three outs.
  if (answer.holed) {
    const lead = doublePlayRunnerBase(state.bases)!;
    state.outs += 2;
    state.doublePlays[baseballFieldingTeam(state)] += 1;
    const advanced = state.outs < 3 ? advanceRunners(state.bases, 1, lead) : { bases: [false, false, false] as BaseballBases, moves: [], runs: 0 };
    state.bases = advanced.bases;
    addRuns(state, team, advanced.runs);
    settle(state, {
      kind: 'doublePlay', team, hole: pending.hole, holed: false,
      moves: [{ from: lead, to: 'out' }, { from: 0, to: 'out' }, ...advanced.moves], runs: advanced.runs, outs: 2,
    }, events);
    return;
  }
  plainOut(state, 'out', pending.hole, events);
}

/**
 * `gateStrike`: the ball hit the gate and never rolled. A batter is out (never
 * a double-play ball); a fielder fails to answer. The read is a miss everywhere.
 */
export function applyBaseballPutt(previous: BaseballState, read: BaseballRead, options: { gateStrike?: boolean } = {}): AppliedBaseballPutt {
  if (previous.phase !== 'batting' && previous.phase !== 'fielding') return { state: previous, events: [] };
  const state = clone(previous);
  const events: BaseballEvent[] = [];
  if (state.phase === 'batting') applyBatter(state, read, events, options.gateStrike === true);
  else applyFielder(state, read, events);
  return { state, events };
}

/** Tied after the last inning: one at-bat each, or another full inning. */
export function chooseBaseballExtraFormat(previous: BaseballState, format: BaseballExtraFormat): BaseballState {
  if (previous.phase !== 'extraChoice') return previous;
  const state = clone(previous);
  if (format === 'inning') {
    state.swingOff = null;
    startHalf(state, 'top', state.inning + 1);
    return state;
  }
  state.swingOff = { outcomes: [null, null] };
  // One at-bat each starts every hit fresh for both teams: the full 3-ft ring,
  // nothing used up (Cole, 2026-10-19). Another inning keeps the stages.
  state.hitStages = [[0, 0, 0, 0], [0, 0, 0, 0]];
  state.hitsEarned = [[false, false, false, false], [false, false, false, false]];
  state.half = 'top';
  state.outs = 0;
  state.bases = [false, false, false];
  state.pending = null;
  state.phase = 'batting';
  return state;
}

/** "T2", "B3": the half and inning for the HUD. */
export function baseballInningLabel(state: BaseballState): string {
  if (state.swingOff) return 'SWING-OFF';
  return `${state.half === 'top' ? 'TOP' : 'BOT'} ${state.inning}`;
}
